/**
 * useAsconShipments — 경원(아스콘) 그날 수주·계근 (Firestore 미러 실시간 구독).
 *
 * useRqcShipments 와 같은 모양(loading·error·lastUpdated·refresh)에 미러 상태(stale)를 더했다.
 * 결과에 (날짜, 구독 번호)를 붙여 두고 지금 날짜·구독과 맞을 때만 보여 준다 — 날짜를 바꾸는 즉시 이전 날짜 행이 사라진다.
 * 수집기(중계 PC)가 쓰는 순간 onSnapshot 으로 들어오므로 폴링하지 않는다.
 * lastUpdated = 수집기 마지막 회차 시각(생존 신호). 3분 넘게 없으면 stale — 화면이 "수집기 응답 없음"을 알린다.
 * date 를 "" 로 주면 아무것도 구독하지 않는다(loading false, 빈 값).
 */
import { useCallback, useEffect, useState } from "react";
import type { AsconOrder, AsconTicket } from "@/domain/asconShipment";
import { asconShipmentRepo, type AsconSource } from "@/services/asconShipment.repository";

/** 생존 신호가 이보다 오래되면 수집기가 멈춘 것으로 본다(수집기는 1분마다 신호) */
const STALE_MS = 3 * 60 * 1000;

export interface UseAsconShipmentsState {
  orders: AsconOrder[];
  tickets: AsconTicket[];
  source: AsconSource | null;
  loading: boolean;
  error: string | null;
  /** ko-KR 시각 HH:MM:SS — 수집기 마지막 회차(가상 자료면 불러온 시각) */
  lastUpdated: string;
  /** lastUpdated 의 시각 원본(상대시각 "갱신 n분 전" 표시용) */
  lastUpdatedAt: Date | null;
  /** 수집기 생존 신호가 3분 넘게 없음(또는 마지막 회차가 OKIS 읽기 실패) */
  stale: boolean;
  /** 다시 구독(화면 새로고침 버튼) */
  refresh: () => void;
}

interface Result {
  date: string;
  tick: number;
  orders: AsconOrder[];
  tickets: AsconTicket[];
  source: AsconSource | null;
  syncedAt: Date | null;
  collectorOk: boolean | null;
  error: string | null;
  at: Date;
}

const EMPTY: AsconOrder[] = [];
const EMPTY_T: AsconTicket[] = [];
const hms = (d: Date) => d.toLocaleTimeString("ko-KR", { hour12: false });

export function useAsconShipments(date: string): UseAsconShipmentsState {
  const [tick, setTick] = useState(0);
  const [res, setRes] = useState<Result | null>(null);
  // 수집기가 멈추면 새 신호가 안 오므로, 오래됨 판정을 위해 1분마다 다시 그린다
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(
    () =>
      // date "" = 끔(useRqcShipments 와 같은 약속) — 홈 위젯이 공간에 따라 중앙/경원 중 하나만 구독하게
      !date ? undefined : asconShipmentRepo.watchDay(
        date,
        (r) => setRes({ date, tick, ...r, error: null, at: new Date() }),
        (e) =>
          setRes({
            date, tick, orders: EMPTY, tickets: EMPTY_T, source: null, syncedAt: null, collectorOk: null,
            error: e.message.includes("permission") ? "경원 출하 자료를 볼 권한이 없습니다" : e.message,
            at: new Date(),
          }),
      ),
    [date, tick],
  );

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const cur = res && res.date === date ? res : null;
  const okis = cur?.source === "okis";
  const stale = okis && (!cur.syncedAt || now - cur.syncedAt.getTime() > STALE_MS || cur.collectorOk === false);
  const staleMsg = stale
    ? `OKIS 수집기 응답 없음 — 마지막 갱신 ${cur?.syncedAt ? hms(cur.syncedAt) : "기록 없음"}. 중계 PC 확인이 필요합니다`
    : null;
  const updatedAt = cur ? (okis && cur.syncedAt ? cur.syncedAt : cur.at) : null;
  return {
    orders: cur?.orders ?? EMPTY,
    tickets: cur?.tickets ?? EMPTY_T,
    source: cur?.source ?? null,
    loading: Boolean(date) && (!cur || cur.tick !== tick),
    error: cur?.error ?? staleMsg,
    lastUpdated: updatedAt ? hms(updatedAt) : "",
    lastUpdatedAt: updatedAt,
    stale: Boolean(stale),
    refresh,
  };
}
